import { AmenityFormLayout } from '@/components/amenities';
import { type AmenityFormData, useAmenityForm } from '@/hooks/useAmenityForm';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormPageLayout,
  Skeleton,
  Textarea,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { toast } from '@startupquickstart/frontend';
import { Copy, FileUp } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

/** Form keys accepted by the dev-tools JSON import. */
const VALID_KEYS: (keyof AmenityFormData)[] = [
  'name', 'amenityTypeId', 'hotelId', 'status', 'featuredImageId',
  'description', 'phoneNumber', 'email', 'website', 'usesHotelAddress',
  'floor', 'capacity', 'streetAddress', 'streetAddress2', 'city',
  'state', 'postalCode', 'country', 'hours',
];

/**
 * Standalone amenity create/edit form page (routes: `/amenities/new`,
 * `/amenities/:id/edit`). Drives the shared {@link useAmenityForm} state and
 * {@link AmenityFormFields} inside the full-page {@link FormPageLayout} shell,
 * plus internal-only JSON import / schema-copy dev tools.
 */
export function AmenityFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isInternal } = useRequireOrganization();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');

  const hotelIdParam = searchParams.get('hotelId') ?? undefined;

  const { formData, setFormData, isLoading, isSaving, lastSaved, handleSubmit } = useAmenityForm({
    amenityId: id,
    hotelId: hotelIdParam,
    onSuccess: amenityId => {
      // Editing returns to where the user came from; creating opens the new amenity.
      if (id) {
        navigate(-1);
      } else {
        navigate(`/amenities/${amenityId}`);
      }
    },
  });

  const handleImportJson = () => {
    try {
      const parsed = JSON.parse(importJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        toast.error('JSON must be an object');
        return;
      }

      const updated: Partial<AmenityFormData> = {};
      for (const key of VALID_KEYS) {
        if (key in parsed) {
          (updated as Record<string, unknown>)[key] = parsed[key];
        }
      }

      if (parsed.amenityType && typeof parsed.amenityType === 'object') {
        updated.amenityType = {
          id: parsed.amenityType.id,
          name: parsed.amenityType.name,
        };
        if (!updated.amenityTypeId && parsed.amenityType.id) {
          updated.amenityTypeId = parsed.amenityType.id;
        }
      }

      if (parsed.hotel && typeof parsed.hotel === 'object') {
        updated.hotel = {
          id: parsed.hotel.id,
          name: parsed.hotel.name,
        };
        if (!updated.hotelId && parsed.hotel.id) {
          updated.hotelId = parsed.hotel.id;
        }
      }

      // If hotelId is in the URL, preserve it over imported data
      if (hotelIdParam) {
        delete updated.hotelId;
        delete updated.hotel;
      }

      setFormData(prev => ({ ...prev, ...updated }));
      setImportDialogOpen(false);
      setImportJson('');
      toast.success('JSON imported successfully');
    } catch {
      toast.error('Invalid JSON format');
    }
  };

  const handleCopySchema = () => {
    const schema = {
      name: 'Rooftop Pool & Lounge',
      description: 'Heated infinity pool with panoramic city views and poolside bar service.',
      status: 'published',
      phoneNumber: '+1-555-123-4567',
      email: 'pool@hotel.com',
      website: 'https://hotel.com/pool',
      usesHotelAddress: true,
      floor: '25',
      capacity: 75,
      streetAddress: '123 Main Street',
      streetAddress2: 'Suite 100',
      city: 'Miami',
      state: 'FL',
      postalCode: '33101',
      country: 'US',
      hours: {
        monday: { ranges: [{ open: '06:00', close: '22:00' }] },
        tuesday: { ranges: [{ open: '06:00', close: '22:00' }] },
        wednesday: { ranges: [{ open: '06:00', close: '22:00' }] },
        thursday: { ranges: [{ open: '06:00', close: '22:00' }] },
        friday: { ranges: [{ open: '06:00', close: '23:00' }] },
        saturday: { ranges: [{ open: '07:00', close: '23:00' }] },
        sunday: { ranges: [{ open: '07:00', close: '21:00' }] },
      },
    };

    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    toast.success('Schema copied to clipboard');
  };

  const handleCancel = () => {
    navigate(id ? `/amenities/${id}` : '/amenities');
  };

  // ---------------------------------------------------------------------------
  // Loading skeleton
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2, 3].map(i => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm gap-0 py-0">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  // ---------------------------------------------------------------------------
  // Internal-only dev tools (create mode), rendered in the header.
  // ---------------------------------------------------------------------------
  const devTools = isInternal && !id ? (
    <>
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (open) setImportJson('');
      }}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" icon={FileUp}>
            Import JSON
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import JSON</DialogTitle>
            <DialogDescription>
              Paste amenity JSON data to pre-fill the form fields.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={importJson}
            onChange={e => setImportJson(e.target.value)}
            placeholder={'{\n  "name": "Pool",\n  "description": "...",\n  "hours": {\n    "monday": { "ranges": [{ "open": "09:00", "close": "17:00" }] },\n    "saturday": { "isClosed": true }\n  }\n}'}
            rows={12}
            className="font-mono text-sm"
          />
          <DialogFooter>
            <div className="flex sm:justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleImportJson}>
                Import
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Button type="button" variant="outline" icon={Copy} onClick={handleCopySchema}>
        Copy Schema
      </Button>
    </>
  ) : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <AmenityFormLayout
      title={id ? 'Edit Amenity' : 'Create Amenity'}
      subtitle={id && formData.name ? formData.name : undefined}
      submitLabel={id ? 'Save Changes' : 'Create Amenity'}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      headerExtras={devTools}
      formData={formData}
      setFormData={setFormData}
    />
  );
}
